import { useState } from "react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import api, { apiError } from "../lib/api";

const inputCls =
  "w-full border-b border-cocoa/25 bg-transparent py-3 text-sm font-light outline-none transition-colors placeholder:text-taupe/50 focus:border-gold";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-taupe";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="contact-page">
      <Seo
        title="Contact — Zelvoria"
        siteName="ZELVORIA"
        description="Write to the Zelvoria concierge. We reply within one business day."
      />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 py-20 md:px-10 md:py-28 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Contact</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 font-serif text-5xl font-light text-ink md:text-6xl">Write to Us</h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-taupe md:text-base">
              For orders, press, partnerships or a simple hello — the Zelvoria concierge reads every note and
              replies within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-12 space-y-8">
              {[
                { label: "Mail", value: "hello@zelvoriaindia.com" },
                { label: "Hours", value: "Monday – Saturday, 10am – 7pm IST" },
                { label: "Location", value: "India" },
              ].map((row) => (
                <div key={row.label} className="flex gap-8 border-b border-beige/70 pb-6">
                  <span className="w-24 shrink-0 text-[10px] uppercase tracking-[0.25em] text-gold">{row.label}</span>
                  <span className="text-sm font-light text-ink">{row.value}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="border border-beige bg-parchment p-8 md:p-12">
            {done ? (
              <div className="py-16 text-center" data-testid="contact-success">
                <p className="font-serif text-3xl font-light italic text-gold">Thank you.</p>
                <p className="mt-4 text-sm font-light text-taupe">
                  Your note has reached us. Our concierge will write back shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label htmlFor="contact-name" className={labelCls}>Full Name</label>
                  <input id="contact-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelCls}>Email</label>
                  <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email-input" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-message" className={labelCls}>Message</label>
                  <textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How may we help?" data-testid="contact-message-input" className={`${inputCls} resize-none`} />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full bg-cocoa py-4 text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gold disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
